import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import LogoSVG from '../../components/LogoSVG';

const stripExt = (n) => n ? n.replace(/\.(mp4|mov|avi|mkv|wmv|webm)$/i, '') : n;

const STATUS_MAP = {
  none:        { label: '상태없음', color: '#666666' },
  pending:     { label: '보류',     color: '#ff4d4d' },
  in_progress: { label: '진행중',   color: '#4d9fff' },
  done:        { label: '완료',     color: '#28c76f' },
};

function formatDuration(seconds) {
  if (!seconds) return '';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

export default function ClientFolder() {
  const navigate = useNavigate();
  const location = useLocation();
  const [videos, setVideos] = useState([]);
  const [projectName, setProjectName] = useState('');
  const [clientName, setClientName] = useState(location.state?.clientName || '');
  const [sliderValue, setSliderValue] = useState(3);
  const [viewMode, setViewMode] = useState('card');
  const [hoveredId, setHoveredId] = useState(null);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const res = await axios.get('/api/client/folder/index.php', { withCredentials: true });
      setVideos(res.data);
      if (res.data[0]?.project_name) setProjectName(res.data[0].project_name);
    } catch (e) {
      if (e?.response?.status === 401) navigate('/enter');
    }
  };

  const cardWidth = 160 + sliderValue * 24;

  return (
    <div style={s.wrap}>
      <div style={s.topbar}>
        <div style={s.topbarLeft}>
          <LogoSVG size={32} />
          <div style={s.divider} />
          <span style={{ ...s.topbarCurrent, display: 'inline-flex', alignItems: 'center', gap: 5 }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></svg>
            {projectName || '…'}
          </span>
        </div>
        <span style={s.clientName}>{clientName}님 만나서 반가워요!</span>
      </div>

      <div style={s.controlRow}>
        <span style={s.sectionLabel}>영상 {videos.length}개</span>
        <div style={s.sliderGroup}>
          {viewMode === 'card' && (
            <>
              <button style={s.sliderBtn} onClick={() => setSliderValue(v => Math.max(1, v - 1))}>
                <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="5" y1="12" x2="19" y2="12"/></svg>
              </button>
              <input type="range" min={1} max={5} value={sliderValue}
                onChange={e => setSliderValue(Number(e.target.value))} style={s.slider} />
              <button style={s.sliderBtn} onClick={() => setSliderValue(v => Math.min(5, v + 1))}>
                <svg width="21" height="21" viewBox="0 0 24 24" fill="currentColor"><path d="M11 5h2v6h6v2h-6v6h-2v-6H5v-2h6V5z"/></svg>
              </button>
              <div style={{ width: 1, height: 16, background: 'rgba(255,255,255,0.15)', margin: '0 4px' }} />
            </>
          )}
          <button style={{ ...s.sliderBtn, color: viewMode === 'card' ? '#a5a3ff' : 'rgba(255,255,255,0.4)' }} onClick={() => setViewMode('card')} title="카드 보기">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>
          </button>
          <button style={{ ...s.sliderBtn, color: viewMode === 'list' ? '#a5a3ff' : 'rgba(255,255,255,0.4)' }} onClick={() => setViewMode('list')} title="리스트 보기">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>
          </button>
        </div>
      </div>

      {viewMode === 'card' ? (
      <div style={s.grid}>
        {videos.map(v => {
          const st = STATUS_MAP[v.status] || STATUS_MAP.none;
          return (
            <div key={v.id}
              style={{ ...s.videoCard, width: cardWidth, borderColor: hoveredId === v.id ? '#5755df' : 'rgba(255,255,255,0.12)', background: hoveredId === v.id ? 'rgba(87,85,223,0.07)' : 'rgba(255,255,255,0.03)', transform: hoveredId === v.id ? 'translateY(-2px)' : 'none', transition: 'border-color .18s, background .18s, transform .18s' }}
              onMouseEnter={() => setHoveredId(v.id)}
              onMouseLeave={() => setHoveredId(null)}
              onClick={() => navigate(`/client/viewer/${v.id}`, { state: { projectName } })}>
              <div style={s.thumbnail}>
                {v.thumbnail_path
                  ? <img src={v.thumbnail_path} alt="" style={s.thumbImg} />
                  : <div style={s.thumbPlaceholder}>
                      <div style={s.playIcon}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3"/></svg>
                      </div>
                      <span style={s.thumbLabel}>썸네일 없음</span>
                    </div>}
                              </div>
              <div style={s.cardBody}>
                <div style={s.cardName}>
                  <span style={s.cardNameSpan}>{stripExt(v.name).length > 17 ? stripExt(v.name).slice(0, 17) + '...' : stripExt(v.name)}</span>
                </div>
                <div style={s.cardMeta}>
                  <span style={s.cardInfo}>버전 {v.version_count || 1}개</span>
                  <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                    {Number(v.has_new_feedback) > 0 && <span style={s.badgeNew}>새 피드백</span>}
                    <span style={s.statusPill}>
                      <span style={{ ...s.statusDot, background: st.color }} />
                      {st.label}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      ) : (
      <div style={s.listWrap}>
        {videos.map(v => {
          const st = STATUS_MAP[v.status] || STATUS_MAP.none;
          return (
            <div key={v.id}
              style={{ ...s.listRow, borderColor: hoveredId === v.id ? '#5755df' : 'rgba(255,255,255,0.12)', background: hoveredId === v.id ? 'rgba(87,85,223,0.07)' : 'rgba(255,255,255,0.03)', transition: 'border-color .18s, background .18s' }}
              onMouseEnter={() => setHoveredId(v.id)}
              onMouseLeave={() => setHoveredId(null)}
              onClick={() => navigate(`/client/viewer/${v.id}`, { state: { projectName } })}>
              <div style={s.listThumb}>
                {v.thumbnail_path
                  ? <img src={v.thumbnail_path} alt="" style={s.thumbImg} />
                  : <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%', color: 'rgba(255,255,255,0.2)' }}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3"/></svg>
                    </div>}
              </div>
              <div style={s.listName}>
                <span style={s.cardNameSpan}>{stripExt(v.name).length > 30 ? stripExt(v.name).slice(0, 30) + '...' : stripExt(v.name)}</span>
              </div>
              <span style={{ ...s.cardInfo, flexShrink: 0 }}>버전 {v.version_count || 1}개</span>
              <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexShrink: 0, width: 130, justifyContent: 'flex-end' }}>
                {Number(v.has_new_feedback) > 0 && <span style={s.badgeNew}>새 피드백</span>}
                <span style={s.statusPill}>
                  <span style={{ ...s.statusDot, background: st.color }} />
                  {st.label}
                </span>
              </div>
            </div>
          );
        })}
      </div>
      )}
    </div>
  );
}

const s = {
  wrap: { minHeight: '100vh', background: '#191919', fontFamily: "'Pretendard', sans-serif", padding: 20, boxSizing: 'border-box' },
  topbar: { borderTop: '1px solid rgba(255,255,255,0.2)', borderBottom: '1px solid rgba(255,255,255,0.2)', padding: '0 18px', height: 56, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 19 },
  topbarLeft: { display: 'flex', alignItems: 'center', gap: 10 },
  divider: { width: 1, height: 18, background: 'rgba(255,255,255,0.15)', flexShrink: 0 },
  topbarCurrent: { fontSize: '13pt', fontWeight: 400, color: '#ffffff', letterSpacing: '0.5px' },
  clientName: { fontSize: '13pt', fontWeight: 400, color: 'rgba(255,255,255,0.35)', letterSpacing: '0.5px' },
  controlRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 17 },
  sectionLabel: { fontSize: 12, color: 'rgba(255,255,255,0.4)' },
  sliderGroup: { display: 'flex', alignItems: 'center', gap: 10, height: 24 },
  sliderBtn: { color: 'rgba(255,255,255,0.5)', background: 'none', border: 'none', cursor: 'pointer', padding: '0 2px', display: 'flex', alignItems: 'center' },
  slider: { width: 90, height: 2, cursor: 'pointer' },
  grid: { display: 'flex', flexWrap: 'wrap', gap: 38, justifyContent: 'flex-start' },
  listWrap: { display: 'flex', flexDirection: 'column', gap: 8 },
  listRow: { display: 'flex', alignItems: 'center', gap: 14, padding: '8px 14px', border: '0.56px solid rgba(255,255,255,0.2)', borderRadius: 10, cursor: 'pointer', minHeight: 48, boxSizing: 'border-box' },
  listThumb: { width: 72, aspectRatio: '16/9', background: '#0e0e0e', borderRadius: 5, overflow: 'hidden', flexShrink: 0 },
  listName: { display: 'flex', alignItems: 'center', gap: 6, flex: 1, minWidth: 0 },
  videoCard: { background: 'transparent', border: '0.56px solid rgba(255,255,255,0.2)', borderRadius: 16, overflow: 'hidden', cursor: 'pointer', display: 'flex', flexDirection: 'column' },
  thumbnail: { width: '100%', aspectRatio: '16/9', background: '#0e0e0e', position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  thumbImg: { width: '100%', height: '100%', objectFit: 'cover' },
  thumbPlaceholder: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 },
  playIcon: { width: 40, height: 40, borderRadius: '50%', border: '1.5px solid rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,0.2)' },
  thumbLabel: { fontSize: 10, color: 'rgba(255,255,255,0.15)', letterSpacing: '0.5px' },
  durationBadge: { position: 'absolute', bottom: 7, right: 8, fontSize: 10, color: 'rgba(255,255,255,0.5)', background: 'rgba(0,0,0,0.5)', padding: '2px 6px', borderRadius: 4 },
  cardBody: { padding: '12px 14px 14px', display: 'flex', flexDirection: 'column', gap: 5 },
  cardName: { display: 'flex', alignItems: 'center', gap: 6 },
  cardNameSpan: { fontSize: '11pt', fontWeight: 400, color: '#fff', flex: 1 },
  cardMeta: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  cardInfo: { fontSize: 11, color: 'rgba(255,255,255,0.3)' },
  badgeNew: { fontSize: 10, padding: '2px 8px', borderRadius: 20, background: 'rgba(87,85,223,0.2)', color: '#a5a3ff', border: '1px solid rgba(87,85,223,0.3)' },
  statusPill: { display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10, color: 'rgba(255,255,255,0.35)' },
  statusDot: { width: 7, height: 7, borderRadius: '50%', flexShrink: 0 },
};
